import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Send, Loader2, Search, Megaphone, Zap } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const wsUrlFor = () => {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  const u = new URL(base);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws/chat";
  return u.toString();
};

const CANNED_REPLIES = [
  { label: "Production update", body: "Your order is now in production. We'll let you know once it's ready." },
  { label: "Ready for pickup", body: "Good news — your order is ready for pickup at our office in Tinago, Naga City. Please bring your order ID." },
  { label: "Payment reminder", body: "Hi! Just a quick reminder that your order is pending payment. Complete it from your dashboard or get back to us if you need help." },
  { label: "Design approved", body: "Your design has been approved. We'll proceed to production right away." },
  { label: "More info needed", body: "Could you share more details on the layout/colors you'd like? Any reference photos would help us nail your concept." },
  { label: "Thanks!", body: "Thank you for choosing Print Ad Venture — let us know if you need anything else." },
];

const AdminChatInbox = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [filter, setFilter] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [bc, setBc] = useState({ title: "", body: "" });
  const [bcSending, setBcSending] = useState(false);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chat/threads");
      setThreads(data || []);
    } catch { toast.error("Failed to load threads"); }
  }, []);

  const openThread = useCallback(async (t) => {
    setActive(t);
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/chat/threads/${t.thread_id}/messages`);
      setMessages(data.messages || []);
      loadThreads();
    } catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  }, [loadThreads]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Deep-link: /admin/chat-inbox?customer=<user_id> auto-opens that customer's thread
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const customerId = params.get("customer");
    if (!customerId) return;
    (async () => {
      try {
        const { data } = await api.post(`/admin/chat/start-with/${customerId}`);
        await loadThreads();
        openThread(data);
        // Clean the URL
        window.history.replaceState({}, "", "/admin/chat-inbox");
      } catch (err) { toast.error(err?.response?.data?.detail || "Failed to start chat"); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket — live inbox updates
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let ws;
    const connect = () => {
      const url = wsUrlFor();
      if (!url) return;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "message" && m.message) {
            // Append to active thread if matches
            setActive((curr) => {
              if (curr && curr.thread_id === m.message.thread_id) {
                setMessages((prev) => prev.find((x) => x.message_id === m.message.message_id) ? prev : [...prev, m.message]);
              }
              return curr;
            });
            loadThreads();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (!cancelled) setTimeout(connect, 4000); };
    };
    connect();
    return () => { cancelled = true; try { ws && ws.close(); } catch { /* ignore */ } };
  }, [user, loadThreads]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || !active) return;
    setSending(true);
    const body = input.trim();
    setInput("");
    try {
      const { data } = await api.post(`/admin/chat/threads/${active.thread_id}/send`, { body });
      setMessages((prev) => prev.find((x) => x.message_id === data.message_id) ? prev : [...prev, data]);
      loadThreads();
    } catch { setInput(body); toast.error("Send failed"); }
    finally { setSending(false); }
  };

  const sendBroadcast = async () => {
    if (!bc.title.trim() || !bc.body.trim()) { toast.error("Title and body required"); return; }
    setBcSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", bc);
      toast.success(`Broadcast delivered to ${data.delivered_to} customers`);
      setBroadcastOpen(false);
      setBc({ title: "", body: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to broadcast"); }
    finally { setBcSending(false); }
  };

  const filteredThreads = threads.filter((t) =>
    !filter || (t.customer_name + " " + t.customer_email).toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-chat-inbox">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Communications</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F]">Chat inbox</h1>
        </div>
        <button onClick={() => setBroadcastOpen(true)}
          data-testid="broadcast-open-btn"
          className="btn-accent inline-flex items-center gap-2 text-sm">
          <Megaphone className="h-4 w-4" /> Send broadcast
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-220px)]">
        {/* Threads list */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col" data-testid="threads-list">
          <div className="p-3 border-b border-[#E2E8F0] relative">
            <Search className="h-3.5 w-3.5 absolute left-6 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Search customers…"
              data-testid="threads-filter"
              className="w-full pl-10 pr-3 py-2 rounded-full bg-[#F4F6FB] border border-transparent focus:border-[#06B6D4] focus:outline-none text-sm" />
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-[#F4F6FB]">
            {filteredThreads.length === 0 ? (
              <p className="p-8 text-center text-sm text-neutral-500">No conversations.</p>
            ) : filteredThreads.map((t) => (
              <button key={t.thread_id} onClick={() => openThread(t)}
                data-testid={`thread-${t.thread_id}`}
                className={`w-full text-left p-3 flex items-start gap-3 hover:bg-[#F8FAFC] transition-colors ${active?.thread_id === t.thread_id ? "bg-[#F4F6FB]" : ""}`}>
                <div className="h-9 w-9 rounded-full bg-[#0B1E3F] text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {(t.customer_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm truncate">{t.customer_name || t.customer_email}</p>
                    {t.unread_count_admin > 0 && (
                      <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#06B6D4] text-[10px] font-bold text-white flex items-center justify-center">
                        {t.unread_count_admin > 9 ? "9+" : t.unread_count_admin}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 truncate mt-0.5">{t.last_message_preview || "—"}</p>
                  {t.last_message_at && (
                    <p className="text-[10px] text-neutral-400 mt-1">{new Date(t.last_message_at).toLocaleString()}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Active thread */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col" data-testid="thread-conversation">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-neutral-500 text-sm">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 mx-auto text-neutral-300 mb-3" />
                <p>Select a conversation to view messages</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#0B1E3F] text-white flex items-center justify-center text-xs font-bold">
                  {(active.customer_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{active.customer_name}</p>
                    <span data-testid="conversation-type-label"
                      className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${active.is_guest ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {active.is_guest ? "General Inquiry" : "Registered User"}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500">{active.customer_email || "No email on file"}</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8FAFC]">
                {loading ? (
                  <p className="text-center text-sm text-neutral-500"><Loader2 className="h-4 w-4 inline animate-spin" /> Loading…</p>
                ) : messages.length === 0 ? (
                  <p className="text-center text-sm text-neutral-500 py-8">No messages yet.</p>
                ) : messages.map((m) => (
                  <div key={m.message_id} data-testid={`admin-msg-${m.message_id}`}
                    className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${m.sender_role === "admin" ? "bg-[#0B1E3F] text-white rounded-br-sm" : "bg-white border border-[#E2E8F0] rounded-bl-sm"}`}>
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_role === "admin" ? "text-white/60" : "text-neutral-400"}`}>
                        {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="border-t border-[#E2E8F0] bg-white">
                {/* Canned replies / quick actions */}
                <div className="px-3 pt-2 flex flex-wrap items-center gap-1.5" data-testid="canned-replies">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-400 mr-1">
                    <Zap className="h-3 w-3" /> Quick
                  </span>
                  {CANNED_REPLIES.map((c) => (
                    <button type="button" key={c.label}
                      onClick={() => setInput((curr) => curr ? `${curr} ${c.body}` : c.body)}
                      data-testid={`canned-reply-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-[11px] px-2 py-1 rounded-full bg-[#F4F6FB] hover:bg-[#06B6D4] hover:text-white transition-colors">
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="p-3 flex items-center gap-2">
                  <input value={input} onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a reply…"
                    data-testid="admin-chat-input"
                    className="flex-1 px-3 py-2 rounded-full border border-[#E2E8F0] focus:outline-none focus:border-[#06B6D4] text-sm" />
                  <button type="submit" disabled={sending || !input.trim()}
                    data-testid="admin-chat-send"
                    className="h-9 w-9 rounded-full bg-[#06B6D4] hover:bg-[#0891B2] text-white flex items-center justify-center disabled:opacity-50">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Broadcast dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="max-w-lg" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-amber-500" /> Send broadcast to all customers</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-neutral-500">
            Every active customer will get a notification (and an entry in their bell).
          </p>
          <input className="pav-input" placeholder="Title (e.g. Holiday Sale)" data-testid="broadcast-title"
            value={bc.title} onChange={(e) => setBc({ ...bc, title: e.target.value })} />
          <textarea className="pav-input min-h-[120px]" placeholder="Your message…" data-testid="broadcast-body"
            value={bc.body} onChange={(e) => setBc({ ...bc, body: e.target.value })} />
          <DialogFooter>
            <button onClick={() => setBroadcastOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={sendBroadcast} disabled={bcSending} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="broadcast-send-btn">
              {bcSending && <Loader2 className="h-4 w-4 animate-spin" />} Send broadcast
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChatInbox;
